/**
 * Precedent: APR Model definition panel — `apps/portal/web/src/pages/apr/evaluation/APRModel.tsx:974-1090`
 * (+ `APRModelPanel.css`). Cloned, not imported (DECISIONS.md D-003). Nothing under pages/apr/
 * or pages/master-data/ is modified by this module.
 *
 * SCREEN_MAP #2 — INI-01-03, MODE-01. The panel's shape is the precedent's, down to the four
 * header controls in their order (Back, Save, Maximize, Close) and the form being the flex
 * column whose BODY scrolls while the header above and the buttons below stay put — "previously
 * the whole dialog scrolled, which is what put the heading out of reach on a short window".
 *
 * The fields are named exactly by `BUILD_INSTRUCTIONS.md` §2.3.B2 `Initiative`: **header, type,
 * status, currency, fiscal year, operating mode, AI on/off**.
 *
 * THREE BEHAVIOURS ARE CLONED FROM `pages/master-data/ApplicationDetailDrawer.tsx`, because
 * UI_CONVENTIONS §5.3 records what each one is for:
 *   · Save KEEPS YOU IN EDIT (`:657-706`). It used to return the panel to view, "so a second
 *     correction meant pressing Edit again and finding your place".
 *   · The draft is RE-SEEDED FROM THE SERVER'S RESPONSE, not left as typed, "so what is on
 *     screen after a save is what was actually stored".
 *   · The "Saved" note does not outlive the edit: any keystroke clears it (`:651-655`).
 *
 * TWO REFUSALS THE SERVER OWNS, AND HOW THEY REACH THE USER (API_CONTRACT §1.6: switch on the
 * CODE, show the `message` verbatim):
 *   · `operating_mode_frozen` (409, §4.6) — so once `operating_mode_frozen_at` is set the
 *     control is replaced by its value and the reason, rather than offering an edit the server
 *     will reject.
 *   · `operating_mode_not_available` (400, §4.4) — only the modes `/context` reports as
 *     available are offered, which is also why the field has no default here: §4.4 is explicit
 *     that the server has none, because "an initiative's world was chosen by a request that did
 *     not mention it" is not acceptable. `/context`'s `default` is what the UI PRESELECTS.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT CHANGED WHEN D-017 BECAME THE MODULE-WIDE STANDARD (2026-10-05). This panel was already a
 * right-side panel — it is where `chrome/FormPanel` was cloned FROM — so three details were
 * brought into line with the shell rather than the panel being rebuilt:
 *
 *   1. THE OVERLAY NO LONGER DIMS, and no longer closes on a click. Both are D-017's whole
 *      point and `FormPanel.css:25-33` states it: the list behind must stay fully visible and
 *      usable, and a `.form-modal`-style click-away "closed on a stray click and took the
 *      half-filled form with it". `.erp-app-panel-overlay` is SHARED (`../source-vocabulary.css`)
 *      and is NOT edited; the modifier that clears it lives in this screen's own stylesheet.
 *   2. THE FOOTER'S SAVE CARRIES THE SAVE ICON — owner's words, *"Save should be icon Save"*.
 *      Same glyph as the header control, at `FormPanel.tsx:52-80`'s 17px.
 *   3. THE QUIET SECONDARY IS LABELLED **Cancel**, as D-017 words it and as every other panel in
 *      the module already reads. It does the same thing it did when it said "Close", and the red
 *      close control in the header is untouched beside it.
 */
import { useCallback, useEffect, useState } from 'react';
import { erpApi } from '../api';
import {
  type ErpContext,
  type Initiative,
  type InitiativeCreate,
  type OperatingMode,
} from '../types';
import '../chrome/panelWidths.css';
import './InitiativeDetailPanel.css';

export interface InitiativeDetailPanelProps {
  /** `null` creates a new initiative; an id opens that one. */
  initiativeId: string | null;
  context: ErpContext | null;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

interface FormState {
  name: string;
  description: string;
  initiative_type: string;
  status: string;
  currency_code: string;
  fiscal_year: string;
  operating_mode: OperatingMode;
  ai_enabled: boolean;
}

/**
 * `status` is free `TEXT` by `0094` and API_CONTRACT §4.3 says the set "is not enumerated —
 * OPEN_ISSUES #5". So it is a text field, not a select: offering a fixed list here would invent
 * the enumeration the contract deliberately does not have. `Draft` is `0094`'s own default.
 */
const DEFAULT_STATUS = 'Draft';

const emptyForm = (mode: OperatingMode): FormState => ({
  name: '',
  description: '',
  initiative_type: '',
  status: DEFAULT_STATUS,
  currency_code: '',
  fiscal_year: '',
  operating_mode: mode,
  ai_enabled: false,
});

const formFrom = (initiative: Initiative): FormState => ({
  name: initiative.name,
  description: initiative.description ?? '',
  initiative_type: initiative.initiative_type ?? '',
  status: initiative.status,
  currency_code: initiative.currency_code ?? '',
  fiscal_year: initiative.fiscal_year ?? '',
  operating_mode: initiative.operating_mode,
  ai_enabled: initiative.ai_enabled,
});

/**
 * The Save glyph, drawn once and used twice — the header control and the footer's primary.
 *
 * The path data is this file's own, unchanged; it is the drawing `chrome/FormPanel.tsx:52-80`
 * cites back to these lines. The header keeps its 19px, the footer takes the shell's 17px.
 */
function SaveGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 20 20" width={size} height={size} aria-hidden="true">
      <path
        d="M4 3h9l3 3v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M6.5 3v4.5h6V3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <rect
        x="6.5"
        y="11"
        width="7"
        height="5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

const MODE_LABEL: Record<OperatingMode, string> = {
  apr_connected: 'APR connected — system definitions come from the APR module, read only',
  standalone: "Standalone — system definitions are this module's own",
};

export function InitiativeDetailPanel({
  initiativeId,
  context,
  onClose,
  onSaved,
  onError,
}: InitiativeDetailPanelProps) {
  const defaultMode = context?.operatingMode.default ?? 'standalone';
  const availableModes = context?.operatingMode.available ?? ['standalone'];

  const [initiative, setInitiative] = useState<Initiative | null>(null);
  const [form, setForm] = useState<FormState>(() => emptyForm(defaultMode));
  const [loading, setLoading] = useState(initiativeId !== null);
  const [saving, setSaving] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [maximized, setMaximized] = useState(false);

  const load = useCallback(async () => {
    if (initiativeId === null) return;
    setLoading(true);
    setPanelError(null);
    try {
      const payload = await erpApi.initiatives.get(initiativeId);
      setInitiative(payload.initiative);
      setForm(formFrom(payload.initiative));
    } catch (err) {
      setPanelError(err instanceof Error ? err.message : 'Failed to load this initiative');
    } finally {
      setLoading(false);
    }
  }, [initiativeId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* Any keystroke clears the acknowledgement — `ApplicationDetailDrawer.tsx:651-655`. */
  const change = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setSavedNote(null);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const frozen = initiative?.operating_mode_frozen_at ?? null;
  const baselined = initiative?.baselined_at ?? null;

  const save = async () => {
    if (!form.name.trim()) {
      onError('Name is required');
      return;
    }
    setSaving(true);
    setPanelError(null);
    try {
      if (initiativeId === null) {
        const body: InitiativeCreate = {
          name: form.name.trim(),
          description: form.description.trim() || null,
          initiative_type: form.initiative_type.trim(),
          status: form.status.trim() || DEFAULT_STATUS,
          currency_code: form.currency_code.trim(),
          fiscal_year: form.fiscal_year.trim(),
          operating_mode: form.operating_mode,
          ai_enabled: form.ai_enabled,
        };
        const payload = await erpApi.initiatives.create(body);
        /* Re-seeded from the server's response, not from what was typed. */
        setInitiative(payload.initiative);
        setForm(formFrom(payload.initiative));
        setSavedNote('Saved');
        onSaved('Initiative created');
      } else {
        const payload = await erpApi.initiatives.patch(initiativeId, {
          name: form.name.trim(),
          description: form.description.trim() || null,
          initiative_type: form.initiative_type.trim(),
          status: form.status.trim() || DEFAULT_STATUS,
          currency_code: form.currency_code.trim(),
          fiscal_year: form.fiscal_year.trim(),
          ai_enabled: form.ai_enabled,
          /* Sending the same value is a no-op, not a conflict (§4.6) — but once the mode is
             frozen the field is not editable at all, so it is simply not sent. */
          ...(frozen ? {} : { operating_mode: form.operating_mode }),
        });
        setInitiative(payload.initiative);
        setForm(formFrom(payload.initiative));
        setSavedNote('Saved');
        onSaved('Initiative updated');
      }
    } catch (err) {
      /* The server's own display-ready text, shown unaltered (§1.6). */
      setPanelError(err instanceof Error ? err.message : 'Failed to save this initiative');
    } finally {
      setSaving(false);
    }
  };

  const creating = initiativeId === null;
  const title = creating
    ? 'Create New Initiative'
    : baselined
      ? 'View Initiative'
      : 'Edit Initiative';
  const subtitle = creating
    ? 'Name the initiative and choose the world it is computed in'
    : baselined
      ? 'This initiative is baselined — its header is shown read-only'
      : 'Update the initiative header, its fiscal context and whether AI is enabled';

  const readOnly = Boolean(baselined);

  return (
    /* The overlay does NOT dim and does NOT close on a click — D-017, and this file's header
       records why. `erp-ini-overlay-clear` is this screen's own modifier over the shared
       `.erp-app-panel-overlay` geometry; the shared rule is not edited (CLAUDE.md §1.2.1b). It
       also gives up its clicks, so the list behind stays usable, which is why there is nothing
       left for a click handler here to catch and the panel's `stopPropagation` guard went with
       it. Close, Back and Cancel are the three ways out, and none of them can be hit by accident. */
    <div className="erp-app-panel-overlay erp-ini-overlay-clear">
      <div className={`erp-model-panel${maximized ? ' erp-model-panel--max' : ''}`}>
        <div className="erp-model-panel__head">
          <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
          {/* Back, Save, Maximize, Close — the precedent's order and its four roles. */}
          <div className="erp-model-panel__controls">
            <button type="button" className="erp-pc erp-pc--back" onClick={onClose}>
              ‹ Back
            </button>
            {!readOnly && (
              <button
                type="button"
                className="erp-pc erp-pc--save"
                title="Save changes"
                aria-label="Save"
                disabled={saving || loading}
                onClick={() => void save()}
              >
                <SaveGlyph size={19} />
              </button>
            )}
            <button
              type="button"
              className="erp-pc erp-pc--icon"
              onClick={() => setMaximized((m) => !m)}
              title={maximized ? 'Restore' : 'Maximize'}
              aria-label={maximized ? 'Restore' : 'Maximize'}
            >
              {maximized ? (
                <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                  <path
                    d="M12 8h4M12 8V4M8 12H4M8 12v4"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                  <path
                    d="M12 4h4v4M8 16H4v-4"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
            <button
              type="button"
              className="erp-pc erp-pc--close"
              onClick={onClose}
              title="Close"
              aria-label="Close"
            >
              <svg viewBox="0 0 20 20" width="19" height="19" aria-hidden="true">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="erp-model-panel__body">
          {panelError && <div className="erp-panel-error">⚠️ {panelError}</div>}

          {loading ? (
            <p>Loading initiative…</p>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="erp-ini-name">Name *</label>
                <input
                  id="erp-ini-name"
                  type="text"
                  value={form.name}
                  disabled={readOnly}
                  onChange={(e) => change('name', e.target.value)}
                  placeholder="e.g., FY27 ERP Consolidation"
                />
              </div>

              <div className="form-group">
                <label htmlFor="erp-ini-description">Description</label>
                <textarea
                  id="erp-ini-description"
                  rows={3}
                  value={form.description}
                  disabled={readOnly}
                  onChange={(e) => change('description', e.target.value)}
                  placeholder="What this consolidation is for"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="erp-ini-type">Type</label>
                  <input
                    id="erp-ini-type"
                    type="text"
                    value={form.initiative_type}
                    disabled={readOnly}
                    onChange={(e) => change('initiative_type', e.target.value)}
                    placeholder="e.g., ERP"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="erp-ini-status">Status</label>
                  <input
                    id="erp-ini-status"
                    type="text"
                    value={form.status}
                    disabled={readOnly}
                    onChange={(e) => change('status', e.target.value)}
                    placeholder={DEFAULT_STATUS}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="erp-ini-currency">Currency</label>
                  <input
                    id="erp-ini-currency"
                    type="text"
                    value={form.currency_code}
                    disabled={readOnly}
                    onChange={(e) => change('currency_code', e.target.value)}
                    placeholder="e.g., USD"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="erp-ini-fiscal">Fiscal year</label>
                  <input
                    id="erp-ini-fiscal"
                    type="text"
                    value={form.fiscal_year}
                    disabled={readOnly}
                    onChange={(e) => change('fiscal_year', e.target.value)}
                    placeholder="e.g., FY27"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="erp-ini-mode">Operating mode</label>
                {frozen ? (
                  <>
                    <div className="erp-field-readonly" id="erp-ini-mode">
                      {MODE_LABEL[form.operating_mode]}
                    </div>
                    <p className="erp-field-note">
                      Frozen when this initiative was baselined. Changing it now would leave every
                      published score rendering against systems from a different source.
                    </p>
                  </>
                ) : (
                  <>
                    <select
                      id="erp-ini-mode"
                      value={form.operating_mode}
                      disabled={readOnly}
                      onChange={(e) => change('operating_mode', e.target.value as OperatingMode)}
                    >
                      {availableModes.map((mode) => (
                        <option key={mode} value={mode}>
                          {MODE_LABEL[mode]}
                        </option>
                      ))}
                    </select>
                    <p className="erp-field-note">
                      {context?.operatingMode.aprLicensed
                        ? 'Both modes are available on this install. The mode is frozen when the initiative is baselined.'
                        : 'The APR module is not licensed on this install, so standalone is the only mode available. The mode is frozen when the initiative is baselined.'}
                    </p>
                  </>
                )}
              </div>

              <div className="form-group">
                <label className="erp-chk" htmlFor="erp-ini-ai">
                  <input
                    id="erp-ini-ai"
                    type="checkbox"
                    checked={form.ai_enabled}
                    disabled={readOnly}
                    onChange={(e) => change('ai_enabled', e.target.checked)}
                  />
                  <span>AI enabled for this initiative</span>
                </label>
                <p className="erp-field-note">
                  Off by default — AI is opt-in per initiative, and it never alters a calculated
                  score.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="erp-model-panel__foot">
          {savedNote && <span className="erp-saved-note">{savedNote}</span>}
          {/* The quiet secondary, worded as D-017 words it. */}
          <button type="button" className="erp-model-panel__btn" onClick={onClose}>
            Cancel
          </button>
          {/* The one filled primary on the panel (CLAUDE.md §1.9.3), and the Save icon rides with
              it — owner's words, *"Save should be icon Save"*. */}
          {!readOnly && (
            <button
              type="button"
              className="erp-model-panel__btn erp-model-panel__btn--primary"
              disabled={saving || loading}
              onClick={() => void save()}
            >
              <SaveGlyph size={17} />
              {saving ? 'Saving…' : creating ? 'Create' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
